import { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { fetchReport } from '../functions/fetchReport';
import { fetchGameStages } from '../functions/stageReport';
import { isUserDemoBypass } from './demoGate';
import {
  hasStageAnswerInReport,
  mergeGameAssignmentIntoUserData,
  resolveNavigatePathFromProgress,
} from './gameStages';

/**
 * On mount, checks fetchReport for whether this stage was already completed.
 * Shows popup and redirects to the next assigned stage when already played.
 */
export function useStageAlreadyPlayedGuard({
  stageNumber,
  submitted = false,
  user,
  adminToken,
  navigate,
  onAlreadyPlayed,
}) {
  const [playCheckReady, setPlayCheckReady] = useState(false);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const popupShownRef = useRef(false);
  const submittedRef = useRef(submitted);
  const onAlreadyPlayedRef = useRef(onAlreadyPlayed);
  const completedCheckKeyRef = useRef(null);

  useEffect(() => {
    submittedRef.current = submitted;
  }, [submitted]);

  onAlreadyPlayedRef.current = onAlreadyPlayed;

  const userId = user?.userId;
  const organizationId = user?.organizationId;
  const sessionId = user?.sessionId;
  const userSource = user?.source;
  const userRole = user?.role;

  const beginSubmit = useCallback(() => {
    submittedRef.current = true;
    Swal.close();
    popupShownRef.current = false;
    setAlreadyPlayed(false);
    setPlayCheckReady(true);
  }, []);

  useEffect(() => {
    const checkKey = [
      stageNumber,
      submitted,
      userId,
      organizationId,
      sessionId,
      adminToken,
      userSource,
      userRole,
    ].join('|');

    if (submitted) {
      Swal.close();
      popupShownRef.current = false;
      setAlreadyPlayed(false);
      setPlayCheckReady(true);
      return undefined;
    }
    if (
      isUserDemoBypass({
        source: userSource,
        role: userRole,
        sessionId,
        organizationId,
      })
    ) {
      setPlayCheckReady(true);
      return undefined;
    }

    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setPlayCheckReady(true);
      return undefined;
    }

    if (completedCheckKeyRef.current === checkKey) {
      return undefined;
    }

    let cancelled = false;
    setPlayCheckReady(false);
    setAlreadyPlayed(false);

    const handleAlreadyPlayed = async (resp) => {
      if (cancelled || submittedRef.current || popupShownRef.current) return;
      popupShownRef.current = true;

      setAlreadyPlayed(true);
      setPlayCheckReady(true);
      if (typeof onAlreadyPlayedRef.current === 'function') {
        onAlreadyPlayedRef.current();
      }

      let path = '/puzzle';
      try {
        const stageData =
          resp?.game != null && resp?.nextDay != null
            ? resp
            : await fetchGameStages({ token, userId, organizationId, sessionId });
        if (cancelled || submittedRef.current) return;

        mergeGameAssignmentIntoUserData({
          game: stageData?.game ?? null,
          currentStage: stageData?.currentStage,
          nextDay: stageData?.nextDay,
          report: resp?.report ?? null,
        });

        path = resolveNavigatePathFromProgress(stageNumber, stageData, resp?.report);
      } catch {
        path = '/puzzle';
      }

      if (cancelled || submittedRef.current) {
        popupShownRef.current = false;
        setAlreadyPlayed(false);
        return;
      }

      void Swal.fire({
        icon: 'info',
        title: 'Already played',
        text: 'You have already played this stage. Click Continue to continue your game.',
        confirmButtonText: 'Continue',
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed && !submittedRef.current) {
          navigate(path, { replace: true });
        }
      });
    };

    void (async () => {
      try {
        const resp = await fetchReport({
          userId,
          token,
          sessionId,
          organizationId,
          currentStage: stageNumber,
        });
        if (cancelled || submittedRef.current) return;

        const played =
          resp?.alreadyPlayed === true ||
          hasStageAnswerInReport(resp?.report, stageNumber);

        if (played) {
          mergeGameAssignmentIntoUserData({
            game: resp?.game ?? null,
            currentStage: resp?.currentStage,
            nextDay: resp?.nextDay,
            report: resp?.report ?? null,
          });
          await handleAlreadyPlayed(resp);
          return;
        }
      } catch {
        /* allow play if report check fails */
      } finally {
        if (!cancelled) {
          completedCheckKeyRef.current = checkKey;
          if (!popupShownRef.current) {
            setPlayCheckReady(true);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    adminToken,
    navigate,
    organizationId,
    sessionId,
    stageNumber,
    submitted,
    userId,
    userRole,
    userSource,
  ]);

  return { playCheckReady, alreadyPlayed, beginSubmit };
}

/** Shared gate: checking message, then not-configured, then stage content. Popup handles already-played UI. */
export function StagePlayGate({
  playCheckReady,
  alreadyPlayed,
  isConfigured,
  notConfiguredMessage,
  children,
}) {
  if (alreadyPlayed) {
    return null;
  }
  if (!playCheckReady) {
    return <p className="main-day-subtitle">Checking your progress…</p>;
  }
  if (!isConfigured) {
    return notConfiguredMessage;
  }
  return children;
}

/** @deprecated Use StagePlayGate instead */
export function StagePlayCheckMessage({ playCheckReady, alreadyPlayed }) {
  if (alreadyPlayed) {
    return null;
  }
  if (!playCheckReady) {
    return <p className="main-day-subtitle">Checking your progress…</p>;
  }
  return null;
}
